/**
 * Arreglo 1.6 (oct-2026) — Ficha del cliente: contacto (WhatsApp / texto / llamar / correo), estadísticas,
 * interruptor «Reserva online» (bloqueo por barbero, sin aviso al cliente), pestañas Citas · Fotos · Sobre · Pagos,
 * «Nueva cita» / «Venta» y edición. Lealtad / paquetes / membresía: «Próximamente».
 */
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, MessageCircle, MessageSquare, Phone, Mail, CalendarPlus, Receipt, Pencil, Trash2, Ban, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { clientsApi } from '@/lib/clientsApi';
import { ClientInitial } from './ClientsPanel';
import { ClientEditSheet } from './ClientEditSheet';
import { ClientDurations } from './ClientDurations';
import { tr } from '@/contexts/LanguageContext';
import { fmtMoney } from '@/utils/money';

const money = (v) => fmtMoney(v);
const TABS = [['bookings', tr("Citas")], ['photos', tr("Fotos")], ['about', 'Sobre'], ['payments', tr("Pagos")]];
const STATUS_ES = { confirmed: tr("Confirmada"), pending: tr("Pendiente"), completed: tr("Completada"), cancelled: tr("Cancelada"), no_show: tr("No asistió"), payment_pending: tr("Pago pendiente") };

const Stat = ({ label, value, testid }) => (
  <div className="bg-background border border-border rounded-xl px-2.5 py-2 text-center" data-testid={testid}>
    <p className="font-display font-bold text-base leading-tight tabular-nums">{value}</p>
    <p className="text-[10px] text-muted-foreground leading-tight">{label}</p>
  </div>
);

const ContactBtn = ({ href, icon: Icon, label, testid, disabled }) => (
  <a href={disabled ? undefined : href} target={href?.startsWith('http') ? '_blank' : undefined} rel="noreferrer" aria-disabled={disabled}
    className={`flex flex-col items-center gap-1 flex-1 py-2 rounded-xl border text-[10px] font-semibold ${disabled ? 'opacity-40 border-border pointer-events-none' : 'border-border hover:border-primary/50 text-foreground'}`} data-testid={testid}>
    <Icon className="w-4 h-4 text-primary" /> {label}
  </a>
);

export const ClientSheet = ({ clientId, onClose, onChanged, onBook, onSell }) => {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('bookings');
  const [tabData, setTabData] = useState({});
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => clientsApi.get(clientId).then(setData).catch((e) => { toast.error(e.message); onClose?.(); }), [clientId, onClose]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!clientId || tabData[tab]) return;
    const fetchers = {
      bookings: () => Promise.all([clientsApi.bookings(clientId, 'upcoming'), clientsApi.bookings(clientId, 'past')]).then(([u, p]) => ({ upcoming: u.items || [], past: p.items || [] })),
      payments: () => clientsApi.payments(clientId),
      photos: () => clientsApi.photos(clientId),
      about: async () => ({}),
    };
    fetchers[tab]().then((r) => setTabData((d) => ({ ...d, [tab]: r }))).catch(() => setTabData((d) => ({ ...d, [tab]: {} })));
  }, [tab, clientId, tabData]);

  useEffect(() => { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = ''; }; }, []);

  const c = data?.client;
  const s = data?.stats || {};
  const digits = (c?.phone || '').replace(/\D/g, '');

  const toggleOnline = async (enabled) => {
    if (!enabled && !window.confirm(tr("¿Bloquear la reserva online de {0}? No se le avisará: verá «No hay horarios disponibles en línea, contacta al barbero». Tú sí podrás agendarlo manualmente.", c.display))) return;
    setBusy(true);
    try { const r = await clientsApi.setOnlineBooking(clientId, enabled); setData((d) => ({ ...d, client: r.client })); onChanged?.(); toast.success(enabled ? tr("Reserva online activada") : tr("Reserva online bloqueada para este cliente")); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(tr("¿Eliminar a {0} de tu base de clientes? Sus citas no se borran.", c.display))) return;
    try { await clientsApi.remove(clientId); toast.success(tr("Cliente eliminado")); onChanged?.(); onClose?.(); } catch (e) { toast.error(e.message); }
  };

  const node = (
    <div className="fixed inset-0 z-[46] bg-background flex flex-col" role="dialog" aria-modal="true" data-testid="client-sheet">
      <header className="flex items-center gap-3 px-4 pt-[max(env(safe-area-inset-top),12px)] pb-3 border-b border-border">
        <button type="button" onClick={onClose} className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center" aria-label={tr("Cerrar")} data-testid="client-sheet-close"><X className="w-5 h-5" /></button>
        <h2 className="font-display text-lg font-bold flex-1 truncate">{tr("Cliente")}</h2>
        {c && <button type="button" onClick={() => setEditing(true)} className="h-9 px-3 rounded-full border border-border text-xs font-semibold inline-flex items-center gap-1.5" data-testid="client-edit-btn"><Pencil className="w-3.5 h-3.5" />{' '}{tr("Editar")}</button>}
      </header>
      {!c ? <div className="p-8 text-center text-sm text-muted-foreground">{tr("Cargando…")}</div> : (
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 pb-[max(env(safe-area-inset-bottom),16px)]">
          <div className="flex items-center gap-3">
            <ClientInitial client={c} size="w-16 h-16 text-2xl" />
            <div className="min-w-0 flex-1">
              <h3 className="font-display text-xl font-bold truncate" data-testid="client-name">{c.display}</h3>
              <p className="text-xs text-muted-foreground truncate">{[c.phone_display || c.phone, c.email].filter(Boolean).join(' · ') || tr("Sin datos de contacto")}</p>
              <div className="flex flex-wrap gap-1 mt-1">
                {c.username && <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary font-semibold">@{c.username}</span>}
                {c.trust?.trusted && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 font-semibold">{tr("De confianza")}</span>}
                {c.discount_pct > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 font-semibold">{tr("Descuento {0}%", c.discount_pct)}</span>}
                {c.hashtags && <span className="text-[10px] text-muted-foreground truncate">{c.hashtags}</span>}
              </div>
            </div>
          </div>

          <div className="flex gap-2" data-testid="client-contact-actions">
            <ContactBtn href={`https://wa.me/${digits}`} icon={MessageCircle} label="WhatsApp" disabled={!digits} testid="client-contact-whatsapp" />
            <ContactBtn href={`sms:${digits}`} icon={MessageSquare} label={tr("Texto")} disabled={!digits} testid="client-contact-sms" />
            <ContactBtn href={`tel:${digits}`} icon={Phone} label={tr("Llamar")} disabled={!digits} testid="client-contact-call" />
            <ContactBtn href={`mailto:${c.email || ''}`} icon={Mail} label={tr("Correo")} disabled={!c.email} testid="client-contact-email" />
          </div>

          <div className="grid grid-cols-4 gap-1.5">
            <Stat label={tr("Citas")} value={s.appointments || 0} testid="client-stat-appointments" />
            <Stat label={tr("No asistió")} value={s.no_shows || 0} testid="client-stat-noshows" />
            <Stat label={tr("Canceladas")} value={s.cancellations || 0} testid="client-stat-cancellations" />
            <Stat label={tr("Gastado")} value={money(s.total_spent)} testid="client-stat-spent" />
          </div>
          <p className="text-[11px] text-muted-foreground -mt-2" data-testid="client-stat-extra">{tr("Ticket promedio {0} · propina promedio {1} · última visita {2} · próximas {3}", money(s.avg_ticket), money(s.avg_tip), s.last_visit || '—', s.upcoming || 0)}</p>

          <div className="flex gap-2">
            <Button onClick={() => onBook?.(c)} className="flex-1 h-11 rounded-full bg-primary text-primary-foreground font-bold" data-testid="client-new-booking-btn"><CalendarPlus className="w-4 h-4 mr-1.5" />{' '}{tr("Nueva cita")}</Button>
            <Button variant="outline" onClick={() => onSell?.(c)} className="flex-1 h-11 rounded-full font-bold" data-testid="client-sale-btn"><Receipt className="w-4 h-4 mr-1.5" />{' '}{tr("Venta")}</Button>
          </div>

          <div className={`rounded-2xl border p-3 flex items-center gap-3 ${c.online_booking_blocked ? 'border-destructive/50 bg-destructive/10' : 'border-emerald-500/40 bg-emerald-500/5'}`} data-testid="client-online-booking-block">
            {c.online_booking_blocked ? <Ban className="w-5 h-5 text-destructive" /> : <CheckCircle2 className="w-5 h-5 text-emerald-400" />}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold">{tr("Reserva online")}</p>
              <p className="text-[11px] text-muted-foreground">{c.online_booking_blocked ? tr("Bloqueada: no ve tus horarios en línea. Tú sí puedes agendarlo.") : tr("Puede reservar contigo desde la app.")}</p>
            </div>
            <Switch checked={!c.online_booking_blocked} disabled={busy} onCheckedChange={toggleOnline} data-testid="client-online-booking-switch" />
          </div>

          <ClientDurations clientId={clientId} durations={c.custom_durations || {}} onChanged={(r) => { setData((d) => ({ ...d, client: { ...d.client, custom_durations: r.custom_durations } })); onChanged?.(); }} />

          <nav className="grid grid-cols-4 gap-1 bg-card border border-border rounded-full p-1" data-testid="client-tabs">
            {TABS.map(([id, label]) => (
              <button key={id} type="button" onClick={() => setTab(id)} aria-pressed={tab === id} className={`h-9 rounded-full text-xs font-semibold ${tab === id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`} data-testid={`client-tab-${id}`}>{label}</button>
            ))}
          </nav>

          {tab === 'bookings' && (
            <div className="space-y-3" data-testid="client-tab-panel-bookings">
              {['upcoming', 'past'].map((scope) => {
                const rows = tabData.bookings?.[scope] || [];
                return (
                  <div key={scope}>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">{scope === 'upcoming' ? tr("Próximas") : 'Anteriores'}</p>
                    {rows.length === 0 && <p className="text-xs text-muted-foreground">{tabData.bookings ? tr("Ninguna.") : tr("Cargando…")}</p>}
                    <ul className="space-y-1">
                      {rows.map((b) => (
                        <li key={b.id} className="flex items-center justify-between gap-2 bg-card border border-border rounded-xl px-3 py-2 text-sm" data-testid={`client-booking-${b.id}`}>
                          <span className="min-w-0"><span className="block font-semibold truncate">{b.service_name}{b.walk_in ? tr("· venta rápida") : ''}</span><span className="block text-[11px] text-muted-foreground">{b.date} · {b.start_time} · {STATUS_ES[b.status] || b.status}</span></span>
                          <span className="text-xs tabular-nums text-right">{money(b.amount_paid || b.total_amount || b.price)}<br /><span className="text-[10px] text-muted-foreground">{b.payment_status === 'paid' ? (b.payment_method === 'cash' ? tr("efectivo") : tr("tarjeta")) : tr("sin pagar")}</span></span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
          {tab === 'photos' && (
            <div data-testid="client-tab-panel-photos">
              {!(tabData.photos?.items || []).length ? <p className="text-xs text-muted-foreground">{tr("Sin fotos todavía. Aparecen la foto de referencia y las fotos de sus reseñas.")}</p> : (
                <div className="grid grid-cols-3 gap-1.5">
                  {tabData.photos.items.map((p, i) => (
                    <a key={i} href={p.url} target="_blank" rel="noreferrer" className="relative aspect-square rounded-xl overflow-hidden border border-border" data-testid={`client-photo-${i}`}>
                      <img src={p.thumbnail_url || p.url} alt="" className="w-full h-full object-cover" loading="lazy" />
                      <span className="absolute bottom-1 left-1 text-[9px] px-1.5 py-0.5 rounded bg-black/60 text-white">{p.label}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
          {tab === 'about' && (
            <div className="space-y-2 text-sm" data-testid="client-tab-panel-about">
              {[[tr("Cumpleaños"), c.birthday], [tr("Dirección"), c.address], [tr("Corte favorito"), c.favorite_cut], [tr("Alergias / cuidado"), c.allergies], [tr("Notas"), c.notes], [tr("Origen"), { manual: tr("Alta manual"), import_csv: tr("Importado (CSV)"), walk_in: tr("Venta rápida"), booking: tr("Cita") }[c.source] || c.source]].map(([k, v]) => (
                <div key={k} className="flex gap-3 bg-card border border-border rounded-xl px-3 py-2"><span className="text-xs text-muted-foreground w-28 flex-shrink-0">{k}</span><span className="text-sm flex-1 whitespace-pre-wrap">{v || '—'}</span></div>
              ))}
              <div className="flex gap-3 bg-card border border-border rounded-xl px-3 py-2"><span className="text-xs text-muted-foreground w-28 flex-shrink-0">{tr("Consentimientos")}</span>
                <span className="text-xs flex-1">{Object.entries(c.consents || {}).filter(([, v]) => v).map(([k]) => ({ marketing_email: tr("Correo promocional"), marketing_sms: 'SMS promocional', photos_public: tr("Fotos públicas") }[k] || k)).join(' · ') || tr("Ninguno registrado")}</span></div>
              <div className="grid grid-cols-3 gap-1.5 pt-1">
                {[tr("Lealtad"), tr("Paquetes"), tr("Membresía")].map((x) => <div key={x} className="rounded-xl border border-dashed border-border p-2 text-center text-[11px] text-muted-foreground" data-testid={`client-soon-${x}`}>{x}<br /><span className="text-[9px] uppercase tracking-wider">{tr("Próximamente")}</span></div>)}
              </div>
              <button type="button" onClick={remove} className="w-full mt-2 h-10 rounded-full border border-destructive/40 text-destructive text-xs font-semibold inline-flex items-center justify-center gap-1.5" data-testid="client-delete-btn"><Trash2 className="w-3.5 h-3.5" />{' '}{tr("Eliminar cliente")}</button>
            </div>
          )}
          {tab === 'payments' && (
            <div className="space-y-1" data-testid="client-tab-panel-payments">
              <p className="text-xs text-muted-foreground mb-1">{tr("Total pagado:")}{' '}<b className="text-foreground" data-testid="client-payments-total">{money(tabData.payments?.total_paid)}</b></p>
              {!(tabData.payments?.items || []).length && <p className="text-xs text-muted-foreground">{tr("Sin pagos registrados.")}</p>}
              {(tabData.payments?.items || []).map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-2 bg-card border border-border rounded-xl px-3 py-2 text-sm" data-testid={`client-payment-${p.id}`}>
                  <span className="min-w-0"><span className="block font-semibold truncate">{p.service_name}</span><span className="block text-[11px] text-muted-foreground">{p.date} · {p.payment_method === 'cash' ? tr("Efectivo") : p.payment_method === 'qr' ? 'QR' : tr("Tarjeta")}{p.payment_status === 'refunded' ? tr("· reembolsado") : ''}</span></span>
                  <span className="tabular-nums font-semibold">{money(p.amount_paid)}{Number(p.tip_amount) > 0 && <span className="block text-[10px] text-muted-foreground font-normal">{tr("propina {0}", money(p.tip_amount))}</span>}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {editing && c && <ClientEditSheet open mode="edit" client={c} onOpenChange={(o) => !o && setEditing(false)} onSaved={() => { setEditing(false); load(); onChanged?.(); }} />}
    </div>
  );
  return createPortal(node, document.body);
};

export default ClientSheet;
